import axios from 'axios';

import { md5 } from '~/core/helpers';

class ImageUtilsError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ImageUtilsError';
  }
}

/**
 * Converts a blob image into a base64 DataURL
 * @example
 *  const dataURL = await ImageUtils.getBase64FromBlob(imageBlob);
 *  console.debug(dataURL) // data:image/jpeg;base64,/9j/2wBD...
 */
export const getBase64FromBlob = async (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = () =>
      reject(new ImageUtilsError('Error occurred reading blob content'));
    reader.readAsDataURL(blob);
  });

/**
 * Fetch an image from an URL, then converts it to a base64 DataURL
 * @example
 *  const dataURL = await ImageUtils.getBase64FromURL('https://www.domain.com/path/to/image.jpg');
 *  console.debug(dataURL) // data:image/jpeg;base64,/9j/2wBD...
 */
export const getBase64FromURL = async (url: string): Promise<string> => {
  try {
    const { data: blob } = await axios.get<Blob>(url, {
      responseType: 'blob',
    });

    return getBase64FromBlob(blob);
  } catch (error) {
    if (axios.isAxiosError(error)) {
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        console.error(error.response.data);
        console.error(error.response.status);
        console.error(error.response.headers);
      } else if (error.request) {
        // The request was made but no response was received
        // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
        // http.ClientRequest in node.js
        console.error(error.request);
      }
    }

    return Promise.reject(error);
  }
};

/**
 * Generate an S3 URL based on a bucket and key for an object
 * @param bucket    the bucket the file is located in
 * @param key       the key for the file
 * @param newStyle  true for the new style url (default)
 * @returns {string}    a generic url to
 */
export const s3URIFromBucketAndKey = (
  bucket: string,
  key: string,
  newStyle = true
) => {
  let uri = null;

  if (newStyle) {
    uri = `https://${bucket}.s3.amazonaws.com/${key}`;
  }
  // this is the deprecated style URL
  else {
    uri = `https://s3.amazonaws.com/${bucket}/${key}`;
  }

  return uri;
};

/**
 * Extract bucket name and file key from s3 url, old or new style
 * @param s3Uri the https://s3.
 * @returns {*[]}
 */
export const bucketAndKeyFromS3URI = (s3Uri: string) => {
  s3Uri = s3Uri.replace(/\+/g, ' ');
  let parts;

  if ((parts = s3Uri.match('^https://s3.amazonaws.com/([^/]+)/(.*)'))) {
    return [parts[1], parts[2]];
  }
  if ((parts = s3Uri.match('^https://(.+).s3.amazonaws.com/(.*)'))) {
    return [parts[1], parts[2]];
  }
  if ((parts = s3Uri.match('^s3://([^/]+)/(.*)'))) {
    return [parts[1], parts[2]];
  }
  if ((parts = s3Uri.match('^arn:aws:s3:::([^/]+)/(.*)'))) {
    return [parts[1], parts[2]];
  }

  return [null, null];
};

export const removeDataUrlPrefix = (url: string) =>
  url.replace(/^data:image\/(png|jpeg|jpg);base64,/, ''); //already a dataURL but needs to have the prefix removed

// ============================================================================
// IMG URL CONSTANTS
// ============================================================================

/**
 * S3 domain prefix to strip or replace.
 */
const S3_DOMAIN = 'https://s3.amazonaws.com';

/**
 * Available optimized image sizes for MD5 mode.
 */
const OPTIMIZED_SIZES = [300, 600, 1200, 2400] as const;

type OptimizedImageSize = (typeof OPTIMIZED_SIZES)[number];

/**
 * Configuration derived from localStorage for image URL rewriting.
 */
interface ImgRewriteConfig {
  mode: 'passthrough' | 'prefix' | 'custom-domain' | 'md5';
  customDomain?: string;
}

// ============================================================================
// IMG URL HELPER FUNCTIONS
// ============================================================================

/**
 * Pattern to match URLs from known asset domains.
 * Note: Using a function to create fresh regex each time to avoid
 * issues with global regex lastIndex state.
 */
const createKnownAssetPattern = (): RegExp =>
  /(hpro-countertops|sage)\.assets/gi;

/**
 * Checks if URL should bypass rewriting (relative or non-asset).
 */
const shouldPassthroughUrl = (url: string): boolean =>
  url[0] === '/' || !createKnownAssetPattern().test(url);

/**
 * Checks if running on localhost for development passthrough.
 */
const isLocalhostEnvironment = (): boolean => /localhost/.test(document.URL);

/**
 * Determines the rewrite configuration from localStorage.
 * Preserves original fallback logic including dead code paths.
 */
const getRewriteConfig = (rewrite: string): ImgRewriteConfig => {
  // Localhost passthrough (original lines 149-151)
  // Note: This condition can only be true if localStorage returned null/empty
  // before the || 'md5' default was applied. Preserved for safety.
  if ((!rewrite || rewrite === '') && isLocalhostEnvironment()) {
    return { mode: 'passthrough' };
  }

  // Prefix mode - strip domain (original lines 154-158)
  if (!rewrite || rewrite === '' || rewrite === 'prefix') {
    return { mode: 'prefix' };
  }

  // Custom domain replacement (original lines 161-165)
  if (/^https?:\/\//.test(rewrite)) {
    return { mode: 'custom-domain', customDomain: rewrite };
  }

  // MD5 mode (original lines 168-199)
  if (rewrite === 'md5') {
    return { mode: 'md5' };
  }

  // Unknown value - passthrough (original line 201)
  return { mode: 'passthrough' };
};

/**
 * Handles prefix mode: strips S3 domain to create relative URL.
 */
const handlePrefixMode = (originalUrl: string): string => {
  const baseUrl = originalUrl.replace(S3_DOMAIN, '');

  return encodeURI(baseUrl);
};

/**
 * Handles custom domain mode: replaces S3 domain with custom domain.
 */
const handleCustomDomainMode = (
  originalUrl: string,
  customDomain: string
): string => {
  const baseUrl = originalUrl.replace(S3_DOMAIN, customDomain);

  return encodeURI(baseUrl);
};

/**
 * Checks if URL contains a thumbnail suffix.
 */
const hasThumbnailSuffix = (url: string): boolean => url.includes('-thumbnail');

/**
 * Selects the appropriate optimized size for the requested width.
 *
 * @param width - Requested width (0 or falsy means auto-select)
 * @param isThumbnail - Whether the original URL had -thumbnail suffix
 * @returns The selected optimized size
 */
const selectOptimizedSize = (
  width: number,
  isThumbnail: boolean
): OptimizedImageSize => {
  const minSize = 300;
  const maxSize = 2400;

  if (width) {
    return (OPTIMIZED_SIZES.find((size) => width <= size) ??
      maxSize) as OptimizedImageSize;
  }

  // No width: thumbnails default to minSize, others to maxSize
  return isThumbnail ? minSize : maxSize;
};

/**
 * Extracts filename and extension from S3 key.
 *
 * @param key - The S3 object key (may be null)
 * @returns Tuple of [baseName, extension] where extension includes the dot
 */
const extractFilenameComponents = (
  key: string | null
): [baseName: string, extension: string] => {
  let baseName = key !== null ? key : '';

  // Extract filename from path
  if (baseName.lastIndexOf('/') >= 0) {
    baseName = baseName.substring(baseName.lastIndexOf('/') + 1);
  }

  // Split extension
  let baseExt = '';

  if (baseName.lastIndexOf('.') >= 0) {
    baseExt = baseName.substring(baseName.lastIndexOf('.'));
    baseName = baseName.substring(0, baseName.lastIndexOf('.'));
  }

  return [baseName, baseExt];
};

/**
 * Handles MD5 mode: transforms URL to CDN-optimized format.
 *
 * CRITICAL: The .replace('+', ' ') intentionally replaces only
 * the FIRST occurrence of '+'. This behavior MUST be preserved.
 */
const handleMd5Mode = (originalUrl: string, width: number): string => {
  const isThumbnail = hasThumbnailSuffix(originalUrl);

  // Remove thumbnail suffix and replace FIRST '+' only (intentional!)
  const baseUrl = originalUrl.replace('-thumbnail', '').replace('+', ' ');
  const md5Hash = md5(baseUrl);

  // Select and format size (always a valid OptimizedImageSize, never 0)
  const adjWidth = selectOptimizedSize(width, isThumbnail);
  const size = `-${adjWidth}w`;

  // Extract filename components
  const [, key] = bucketAndKeyFromS3URI(baseUrl);
  const [baseName, baseExt] = extractFilenameComponents(key);

  const newUrl = `/productimg/${md5Hash}/${baseName}${size}${baseExt}`;

  return encodeURI(newUrl);
};

// ============================================================================
// MAIN EXPORTED FUNCTION
// ============================================================================

/**
 * Modify an "original image" url to a "generated product image" URL.
 * If the original is a -thumbnail use 300w (consistent with historical use).
 * Otherwise, use a maximum size of 2400w.
 *
 * THIS IS A WORKAROUND - IT SHOULD ONLY BE USED FOR OUR OWN PRODUCT IMAGES
 *
 * @param originalUrl - The S3 or other image URL to transform
 * @param width - The desired width to select (default: 300)
 * @returns The transformed URL, or empty string for null input
 */
export const imgUrl = (originalUrl: string | null, width = 300): string => {
  // Handle null input
  if (originalUrl === null) {
    return '';
  }

  // Passthrough relative URLs and non-asset URLs unchanged
  if (shouldPassthroughUrl(originalUrl)) {
    return encodeURI(originalUrl);
  }

  // Determine rewrite mode from localStorage
  const rewrite = localStorage.getItem('imgRewrite') || 'md5';
  const config = getRewriteConfig(rewrite);

  // Route to appropriate handler based on mode
  switch (config.mode) {
    case 'prefix':
      return handlePrefixMode(originalUrl);

    case 'custom-domain':
      return handleCustomDomainMode(originalUrl, config.customDomain as string);

    case 'md5':
      return handleMd5Mode(originalUrl, width);

    case 'passthrough':
    default:
      return encodeURI(originalUrl);
  }
};
